import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button, CopyButton, Modal, ProviderLogo } from '@/components/ui';
import { useInvalidateIdentity } from '@/hooks/useIdentityCredentials';
import { deriveLogoSlug } from './identity-lib';
import type { OAuthDeviceStart, ProviderSummary } from '@/types/identity';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Only id + displayName are used (logo slug + copy), so a row that holds a
   *  credential but no full catalog entry can pass a subset. */
  provider: Pick<ProviderSummary, 'id' | 'displayName'>;
  device: OAuthDeviceStart;
}

/**
 * Device-authorization sign-in modal (e.g. AWS IAM Identity Center / `aws-sso`).
 *
 * The provider's `start` route returns a `verificationUriComplete` (with the
 * `userCode` embedded) instead of a redirect URL. We surface the code and an
 * "Open sign-in" button — opening the tab from a *real click* keeps it out of
 * the popup blocker, which a `window.open` after the start `await` would hit.
 *
 * The modal stays open while the user approves in the other tab. There is no
 * token polling on the client: optr-identity completes the device flow
 * server-side. When the user comes back and clicks **Done**, we refetch the
 * identity credentials so the freshly-connected aws-sso session appears.
 */
export default function DeviceAuthModal({ isOpen, onClose, provider, device }: Props) {
  const invalidate = useInvalidateIdentity();
  const [opened, setOpened] = useState(false);
  const [reloading, setReloading] = useState(false);

  // Absolute deadline, fixed at mount so the countdown isn't reset by re-renders.
  const deadline = useMemo(() => Date.now() + device.expiresIn * 1000, [device.expiresIn]);
  const [remainingMs, setRemainingMs] = useState(() => deadline - Date.now());

  useEffect(() => {
    const id = setInterval(() => setRemainingMs(deadline - Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  const openSignIn = () => {
    setOpened(true);
    window.open(device.verificationUriComplete, '_blank', 'noopener,noreferrer');
  };

  // Done = "I've approved." Refetch the credentials list so the new connection
  // shows up, then close. The refetch is awaited so the button can show a
  // loading state rather than closing on a still-stale list.
  const handleDone = async () => {
    setReloading(true);
    try {
      await invalidate();
    } finally {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Connect ${provider.displayName}`}
      size="md"
      footer={
        <Button onClick={handleDone} isLoading={reloading}>
          Done
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <ProviderLogo
            slug={deriveLogoSlug(provider.id, provider.displayName)}
            displayName={provider.displayName}
            className="h-8 w-8"
            grayscale
          />
          <p className="text-sm text-muted-foreground">
            Sign in to {provider.displayName} to finish connecting. Confirm the
            code below matches what's shown after you continue.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Your code</p>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <span className="font-mono text-2xl font-semibold tracking-[0.2em] text-foreground">
              {device.userCode}
            </span>
            <CopyButton value={device.userCode} label="Copy" size="md" />
          </div>
        </div>

        <Button onClick={openSignIn} className="w-full">
          <ExternalLink className="mr-2 h-4 w-4" />
          Open {provider.displayName} sign-in
        </Button>

        <p className="text-xs text-muted-foreground">
          {opened
            ? 'Once you’ve approved in the other tab, come back here and click Done to load your connection.'
            : 'Open the sign-in page and approve the request, then come back and click Done.'}
          {remainingMs > 0 && (
            <>
              {' '}
              <span className="text-foreground">
                Code expires in {formatCountdown(remainingMs)}.
              </span>
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}

/** `m:ss` countdown for the device-code expiry hint. */
function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
